/**
 * Front-end model representing a layer configuration. See back-end complement for field descriptions.
 *
 * ## Fields
 * Name | Description
 * --- | ---
 * serviceLayers | The array of service layers present in this configuration. Initially, this is an empty array.
 * workingLayer | The `WorkspaceLayer` representing the container for temporary objects.
 *
 * ## Events
 * Name | Arguments | Description
 * service-added | MapServiceBase, Integer | Event which fires when a service is added to this model. The event is supplied with the service added and the index in `serviceLayers` at which it was added.
 * service-removed | MapServiceBase, Integer | Event which fires when a service is removed from this model. The event is supplied with the service removed and the index in `serviceLayers` from which it was removed.
 * service-moved | MapServiceBase, Integer, Integer | Event which fires when a service is moved within this model. The event is supplied with the service that was moved, the old index, and the new index.
 * service-visibility-changed | MapServiceBase, boolean | Event which fires when the visibility of a service changes within this model. The event is supplied with the service and the new visibility value.
 * layer-added | MapServiceBase, [MapLayerBase], MapLayerBase, Integer | Event which fires when a layer is added to this model, either as a result of a service being added or directly. The event is supplied with the service to which the layer was added, the layer to which the layer was added (if any), the layer that was added, and the index in the parent collection to which the layer was added.
 * layer-removed | MapServiceBase, [MapLayerBase], MapLayerBase, Integer | Event which fires when a layer is removed from this model, either as a result of a service being added or directly. The event is supplied with the service from which the layer was removed, the layer from which the layer was removed (if any), the layer that was removed, and the index in the parent collection from which the layer was removed.
 * layer-moved | MapServiceBase, [MapLayerBase], MapLayerBase, Integer, Integer | Event which fires when a layer is moved within this model. The event is supplied with the service in which the layer was moved, the parent layer in which the layer moved, if any, the layer that was moved, the original index of the layer, and the new index of the layer.
 * layer-visibility-changed | MapServiceBase, [MapLayerBase], boolean | Event which fires when the visibility of a layer changes within this model. The event is supplied with the service containing the layer, the layer, and the new visibility value.
 *
 * The following Field model attribute sets are located under the static `fields` attribute.
 * * name
 * * description
 * @module app/models/Layout
 */
import Backbone from "backbone";
import _ from "underscore";
import async from "async";
import ServiceFactory from "app/models/map/ServiceFactory";
import $ from "jquery";
import MapServiceCollection from "app/models/MapServiceCollection";
import WorkspaceLayer from "app/models/map/WorkspaceLayer";
import FieldUtils from "app/utils/FieldUtils";
import session from "app/Session";
import urlService from "app/UrlService";
var serviceCollection = new MapServiceCollection();

function loadServices() {
  var services = new MapServiceCollection([]);
  var result = new $.Deferred();

  services.fetch({
    success: function () {
      serviceCollection = services;
      result.resolve(services);
    },
    error: _.bind(result.reject, result),
  });

  return result.promise();
}

var Layout = Backbone.Model.extend(
  {
    urlRoot: function () {
      return urlService.apiurl("/layerconfiguration");
    },

    defaults: function () {
      return {
        name: "",
        description: "",
        configuration: [],
      };
    },

    initialize: function (attributes, options) {
      Backbone.Model.prototype.initialize.apply(this, arguments);

      this.options = _.defaults({}, options);

      if (_.isString(this.options.url) || _.isFunction(this.options.url)) {
        this.url = this.options.url;
      }

      this.serviceLayers = [];
      this.workingLayer = new WorkspaceLayer();
      this.workingLayer.on(
        "visibility-changed",
        this._onLayerVisibilityChanged,
        this
      );

      this._activeService = null;
      this._activeLayer = this.workingLayer;
      this._drawingLayer = this.workingLayer;
      this._selectedLayers = [];

      Object.defineProperty(this, "serviceModels", {
        get: function () {
          return serviceCollection;
        },
      });
    },

    /**
     * Gets a value indicating whether or not all selected layers are associated with external services. If there is no selection, this returns false.
     * @returns {boolean} True if all selected layers are associated with external services, otherwise false.
     */
    allExternal: function () {
      var layers = this.getSelectedLayers();

      return (
        layers.length &&
        !_.any(layers, function (layer) {
          return (
            layer.serviceLayer && !layer.serviceLayer.service.get("external")
          );
        })
      );
    },

    /**
     * Gets a value indicating whether or not all selected layers are associated with internal services. If there is no selection, this returns false.
     * @returns {boolean} True if all selected layers are associated with internal services, otherwise false.
     */
    allInternal: function () {
      var layers = this.getSelectedLayers();

      return (
        layers.length &&
        !_.any(layers, function (layer) {
          return (
            layer.serviceLayer && layer.serviceLayer.service.get("external")
          );
        })
      );
    },

    /**Stores the service configuration into this object.*/
    storeConfiguration: function () {
      var config = [];

      _.each(this.serviceLayers, function (service) {
        var serviceConfig = {};

        serviceConfig.id = service.service.id;
        serviceConfig.visible = service.isVisible();
        serviceConfig.selectable = service.isSelectable();
        serviceConfig.layers = []; //use an array here just in case we need to maintain order
        service.eachLayer(function (layer) {
          var layerConfig = {};

          layerConfig.id = (layer.info || layer.featureLayer).id;
          layerConfig.visible = layer.isVisible();
          layerConfig.selectable = layer.isSelectable();
          serviceConfig.layers.push(layerConfig);
        }, true);
        config.push(serviceConfig);
      });
      this.set("configuration", config);
    },

    /**
     * Loads the service configuration from this object.
     * @async
     * @param  {Function} [cb] The callback. First argument will contain error, if any, and second argument will contain a services array equivalent to the one passed to storeConfiguration.
     * @return {Deferred}      A jQuery Deferred object which fails with the error or finishes with the service array.
     */
    loadConfiguration: function (cb) {
      cb = cb || function () {};
      var deferred = new $.Deferred();
      var _cb = function (err, services) {
        if (err) {
          cb(err);

          deferred.reject(err);
        } else {
          cb(null, services);
          deferred.resolve(services);
        }
      };

      var config = this.get("configuration"); //assume config is in the same format as would be saved by storeConfiguration
      var self = this;
      var newServices = [];

      this.clearServices();

      loadServices().always(function () {
        async.eachSeries(
          config,
          function (serviceConfig, cb) {
            var serviceModel = serviceCollection.get(serviceConfig.id);

            if (serviceModel) {
              var service = ServiceFactory.createService(serviceModel);

              service
                .load()
                .done(function () {
                  var layers = {};
                  /*layer id -> layerConfig*/

                  _.each(serviceConfig.layers, function (layerConfig) {
                    layers[layerConfig.id] = layerConfig;
                  });

                  if (_.isBoolean(serviceConfig.visible)) {
                    service.setVisible(serviceConfig.visible);
                  } else {
                    service.setVisible(serviceModel.get("defaultVisibility"));
                  }

                  if (_.isBoolean(serviceConfig.selectable)) {
                    service.setSelectable(serviceConfig.selectable);
                  } else {
                    service.setSelectable(true); //default should be true
                  }

                  service.eachLayerAsync(
                    function (layer, parents, cb) {
                      layer
                        .load()
                        .done(function () {
                          var model = layer.info || layer.featureLayer;
                          var layerConfig = layers[model.id];

                          if (layerConfig) {
                            layer.setVisible(layerConfig.visible);

                            if (_.isBoolean(layerConfig.selectable)) {
                              layer.setSelectable(layerConfig.selectable);
                            } else {
                              layer.setSelectable(true); //default should be true
                            }
                          }

                          cb();
                        })
                        .fail(function (err) {
                          cb(err);
                        });
                    },
                    true,
                    function (err) {
                      if (err) {
                        return cb(err);
                      }

                      newServices.push(service);
                      cb();
                    }
                  );
                })
                .fail(function (err) {
                  cb(err);
                });
            } else {
              cb();
            }
          },
          function (err) {
            if (err) {
              return _cb(err);
            }

            self.clearServices();

            async.eachSeries(
              newServices,
              function (newService, cb) {
                self
                  .addService(newService)
                  .done(function () {
                    cb();
                  })
                  .fail(function (err) {
                    cb(err);
                  });
              },
              function (err) {
                if (err) {
                  return _cb(err);
                }

                _cb(null, self.serviceLayers);
              }
            );
          }
        );
      });

      return deferred;
    },

    /**
     * Handler for InternalMapService#event:layer-moved
     * @private
     */
    _onServiceLayerMoved: function (service, layer, oldIndex, newIndex) {
      this.trigger("layer-moved", service, null, layer, oldIndex, newIndex);
    },

    /**
     * Handler for InternalMapService#event:layer-added
     * @private
     */
    _onServiceLayerAdded: function (service, layer, index) {
      this.addLayer(service, null, layer, index);
    },

    /**
     * Handler for InternalMapService#event:layer-removed
     * @private
     */
    _onServiceLayerRemoved: function (service, layer, index) {
      if (this.getActiveLayer() == layer) {
        this.setActiveLayer(null);
      }

      if (this.getDrawingLayer() == layer) {
        this.setDrawingLayer(null);
      }

      this.unselectLayer(layer);
      this._detachLayer(layer);
      this.trigger("layer-removed", service, null, layer, index);
    },

    /**
     * Handler for MapServiceBase#event:visibility-changed
     * @private
     */
    _onServiceVisibilityChanged: function (service, visible) {
      this.trigger("service-visibility-changed", service, visible);
    },

    /**
     * Handler for ObjectLayer#event:child-moved
     * @private
     */
    _onLayerChildMoved: function (parent, layer, oldIndex, newIndex) {
      this.trigger(
        "layer-moved",
        parent.serviceLayer,
        parent,
        layer,
        oldIndex,
        newIndex
      );
    },

    /**
     * Handler for ObjectLayer#event:child-added
     * @private
     */
    _onLayerChildAdded: function (parent, layer, index) {
      this.addLayer(parent.serviceLayer, parent, layer, index);
    },

    /**
     * Handler for ObjectLayer#event:child-removed
     * @private
     */
    _onLayerChildRemoved: function (parent, layer, index) {
      if (this.getActiveLayer() == layer) {
        this.setActiveLayer(null);
      }

      if (this.getDrawingLayer() == layer) {
        this.setDrawingLayer(null);
      }

      this.unselectLayer(layer);
      this._detachLayer(layer);
      this.trigger("layer-removed", parent.serviceLayer, parent, layer, index);
    },

    _onLayerVisibilityChanged: function (layer, visible) {
      this.trigger(
        "layer-visibility-changed",
        layer.serviceLayer,
        layer,
        visible
      );
    },

    /**
     * Attaches event handlers to the given service.
     * @param {MapServiceBase} service The service to which events should be attached.
     * @private
     */
    _attachService: function (service) {
      service.on("layer-moved", this._onServiceLayerMoved, this);
      service.on("layer-added", this._onServiceLayerAdded, this);
      service.on("layer-removed", this._onServiceLayerRemoved, this);
      service.on("visibility-changed", this._onServiceVisibilityChanged, this);
    },

    /**
     * Detaches event handlers from the given service.
     * @param {MapServiceBase} service The service from which events should be detached.
     * @private
     */
    _detachService: function (service) {
      service.off("layer-moved", this._onServiceLayerMoved, this);
      service.off("layer-added", this._onServiceLayerAdded, this);
      service.off("layer-removed", this._onServiceLayerRemoved, this);
      service.off("visibility-changed", this._onServiceVisibilityChanged, this);
    },

    /**
     * Attaches event handlers to the given layer.
     * @param {MapLayerBase} layer The layer to which events should be attached.
     * @private
     */
    _attachLayer: function (layer) {
      layer.on("child-moved", this._onLayerChildMoved, this);
      layer.on("child-added", this._onLayerChildAdded, this);
      layer.on("child-removed", this._onLayerChildRemoved, this);
      layer.on("visibility-changed", this._onLayerVisibilityChanged, this);
    },

    /**
     * Detaches event handlers from the given layer.
     * @param {MapLayerBase} layer The layer from which events should be detached.
     * @private
     */
    _detachLayer: function (layer) {
      layer.off("child-moved", this._onLayerChildMoved, this);
      layer.off("child-added", this._onLayerChildAdded, this);
      layer.off("child-removed", this._onLayerChildRemoved, this);
      layer.off("visibility-changed", this._onLayerVisibilityChanged, this);
    },

    /**
     * Add a service to the collection.
     * @param {MapServiceBase} service The service to add to the collection.
     * @param {Integer} [index] The index at which to add the service. By default, services are added to the end of the collection.
     * @return {Promise} A promise which completes when the service is added and fails with the error if an error occurs while adding the service.
     */
    addService: function (service, index) {
      var deferred = new $.Deferred();
      var promise = deferred.promise();
      var self = this;

      //these operations should go before the layer additions, so that a tree can be constructed easily
      //if they were at the end, listeners would potentially have to wait for the service to be added
      //in order to add a layer
      if (_.isNumber(index)) {
        this.serviceLayers.splice(index, 0, service);
        this.trigger("service-added", service, index);
      } else {
        this.serviceLayers.push(service);
        this.trigger("service-added", service, this.serviceLayers.length - 1);
      }

      async.each(
        service.layers,
        function (layer, cb) {
          self
            .addLayer(service, layer)
            .done(function () {
              cb();
            })
            .fail(function (err) {
              cb(err);
            });
        },
        function (err) {
          if (err) {
            return deferred.reject(err);
          }

          self.trigger("service-loaded", service);
          deferred.resolve();
        }
      );

      this._attachService(service);

      return promise;
    },

    /**
     * Move the service at `oldIndex` to `newIndex`. Fires the service-moved event. If `oldIndex` is invalid, this function does nothing.
     * @param {Integer} oldIndex The index of the service to move.
     * @param {Integer} newIndex The destination index for the service.
     */
    moveService: function (oldIndex, newIndex) {
      if (oldIndex >= 0 && oldIndex < this.serviceLayers.length) {
        var result = this.serviceLayers.splice(oldIndex, 1);

        this.serviceLayers.splice(newIndex, 0, result[0]);

        this.trigger("service-moved", result[0], oldIndex, newIndex);
      }
    },

    /**
     * Removes a service from this model. Note that this does not perform any database operations on the service.
     * @param  {MapServiceBase} service The service to remove.
     */
    removeService: function (service) {
      var index = this.serviceLayers.indexOf(service);

      _.each(
        service.layers,
        function (layer) {
          this.removeLayer(service, layer);
        },
        this
      );

      this.serviceLayers.splice(index, 1);

      if (this.getActiveService() == service) {
        this.setActiveService(null);
      }

      this._detachService(service);

      this.trigger("service-removed", service, index);
    },

    /**
     * Removes all services from the layout.
     */
    clearServices: function () {
      while (this.serviceLayers.length) {
        this.removeService(this.serviceLayers[this.serviceLayers.length - 1]);
      }
    },

    /**
     * Add a layer to a service in the collection.
     * @param {InternalMapService} service The service to which to add the layer.
     * @param {EditableMapLayer} [parentLayer] The layer to which to add the layer.
     * @param {EditableMapLayer} layer   The layer to add.
     * @param {Integer} [index]   The index at which to add the layer. By default, layers are added to the end of the list.
     * @return {Promise} A promise which completes when the layer is added and fails with the error if an error occurs while adding the layer.
     */
    addLayer: function (service, parentLayer, layer, index) {
      if (!_.isObject(layer)) {
        index = layer;
        layer = parentLayer;
        parentLayer = undefined;
      }

      var self = this;
      var deferred = new $.Deferred();
      var promise = deferred.promise();

      //the reason for placing these operations here is similar to the reasoning for the analogous operations
      //in addService
      function triggerEvents() {
        if (_.isNumber(index)) {
          self.trigger("layer-added", service, parentLayer, layer, index);
        } else {
          self.trigger(
            "layer-added",
            service,
            parentLayer,
            layer,
            (parentLayer ? parentLayer.children : service.layers).length - 1
          );
        }
      }

      if (layer.children && layer.children.length) {
        triggerEvents();

        async.each(
          layer.children,
          function (layer, cb) {
            layer
              .load()
              .done(function () {
                cb();
              })
              .fail(function (err) {
                cb(err);
              });
          },
          function (err) {
            if (err) {
              return deferred.reject(err);
            }

            var promise = $.when();

            _.each(layer.children, function (layer) {
              var result = self.addLayer(service, layer.parent, layer);

              promise = $.when(promise, result);
            });

            promise
              .done(function () {
                self._attachLayer(layer);

                deferred.resolve();
              })
              .fail(function (err) {
                deferred.reject(err);
              });
          }
        );
      } else {
        if (!layer.parent) {
          //we *don't* want to add if they have a parent, since they will get added when the parent is added.
          promise = layer.load().then(function () {
            self._attachLayer(layer);
            triggerEvents();
          });
        } else {
          promise = $.when(layer);
          triggerEvents();
        }
      }

      return promise;
    },

    /**
     * Moves the layer at the given `oldIndex` in the given `oldParent` (if provided) in the given `oldService` to the provided destination.
     * @param {InternalMapService} oldService The current service in which the layer resides.
     * @param {ObjectLayer} oldParent The current parent layer in which the layer resides. May be null for root layers.
     * @param {Integer} oldIndex The current index at which the layer resides.
     * @param {InternalMapService} newService The destination service.
     * @param {ObjectLayer} newParent The destination layer.
     * @param {Integer} newIndex The destination index.
     */
    moveLayer: function (
      oldService,
      oldParent,
      oldIndex,
      newService,
      newParent,
      newIndex
    ) {
      var destService;
      var moved = false;

      if (oldService == newService) {
        destService = oldService;

        if (oldParent == newParent) {
          if (oldParent) {
            //both oldParent and newParent are not null
            if (oldParent.moveChildLayer) {
              //if it is a model to model move, this function won't exist
              oldParent.moveChildLayer(oldIndex, newIndex);
            }
          } else {
            //both oldParent and newParent are null
            oldService.moveLayer(oldIndex, newIndex);
          }
          moved = true;
        }
      } else {
        destService = newService;
      }

      if (!moved) {
        var layer;

        if (oldParent) {
          layer = oldParent.removeChildLayer(oldIndex);
        } else {
          layer = oldService.removeLayer(oldIndex);
        }

        if (newParent) {
          newParent.addChildLayer(layer, newIndex);
        } else {
          destService.addLayer(layer, newIndex);
        }
      }
      return layer;
    },

    /**
     * Removes a layer from this model. Note that this does not perform any database operations on the layer.
     * @param  {InternalMapService} service     The service from which the layer was removed.
     * @param  {EditableMapLayer} [parentLayer] The parent layer from which the layer was removed.
     * @param  {EditableMapLayer} layer       The layer which was removed.
     */
    removeLayer: function (service, parentLayer, layer) {
      if (!_.isObject(layer)) {
        layer = parentLayer;
        parentLayer = undefined;
      }

      var index;

      if (parentLayer) {
        index = parentLayer.children.indexOf(layer);
      } else if (service) {
        index = service.layers.indexOf(layer);
      } else {
        index = -1;
      }

      if (layer.children && layer.children.length) {
        _.each(
          layer.children,
          function (childLayer) {
            this.removeLayer(service, layer, childLayer);
          },
          this
        );
      }

      if (this.getActiveLayer() == layer) {
        this.setActiveLayer(null);
      }

      if (this.getDrawingLayer() == layer) {
        this.setDrawingLayer(null);
      }

      this.unselectLayer(layer);

      this.trigger("layer-removed", service, parentLayer, layer, index);
    },

    /**
     * Adds the given layer to the set of selected layers.
     * @param  {MapLayerBase} layer The layer to select.
     */
    selectLayer: function (layer) {
      this._selectedLayers.push(layer);
      this.trigger("update-selection", this.getSelectedLayers());
    },

    /**
     * Removes the given layer from the set of selected layers.
     * @param  {MapLayerBase} layer The layer to unselect.
     */
    unselectLayer: function (layer) {
      var layerCount = this._selectedLayers.length;

      this._selectedLayers = _.without(this._selectedLayers, layer); //remove all references to the layer model

      if (this._selectedLayers.length != layerCount) {
        this.trigger("update-selection", this.getSelectedLayers());
      }
    },

    /**
     * Gets the set of selected layers.
     * @return {Array} An array of `MapLayerBase`.
     */
    getSelectedLayers: function () {
      return this._selectedLayers;
    },

    /**
     * Gets the "active" service in this configuration.
     * @return {MapServiceBase} The active service.
     */
    getActiveService: function () {
      return this._activeService;
    },

    /**
     * Sets the "active" service in this configuration.
     * @param {MapServiceBase} service The new active service. Set to null to clear the active service.
     */
    setActiveService: function (service) {
      this._activeService = service;

      if (service) {
        this.trigger("activate-service", service);
      }
    },

    /**
     * Gets the "active" layer in this configuration. Default is the working layer.
     * @return {MapLayerBase} The active layer.
     */
    getActiveLayer: function () {
      return this._activeLayer || this.workingLayer;
    },

    /**
     * Sets the "active" layer in this configuration.
     * @param {MapLayerBase} layer The new active layer. Set to null to set the active layer to the working layer.
     */
    setActiveLayer: function (layer) {
      this._activeLayer = layer;

      if (layer) {
        this.trigger("activate-layer", layer);
      } else {
        this.trigger("activate-layer", this.workingLayer);
      }
    },

    /**
     * Gets the layer to use for drawing.
     * @return {MapLayerBase} The drawing layer.
     */
    getDrawingLayer: function () {
      return this._drawingLayer || this.workingLayer;
    },

    /**
     * Sets the layer to use for drawing.
     * @param {MapLayerBase} layer The new drawing layer.
     */
    setDrawingLayer: function (layer) {
      this._drawingLayer = layer;

      if (layer) {
        this.trigger("activate-drawing-layer", layer);
      } else {
        this.trigger("activate-drawing-layer", this.workingLayer);
      }
    },

    /**
     * Creates a clone of this object. Active and drawing layers/services are not retained.
     *
     * Note that `storeConfiguration` will be run as a side effect of this operation.
     * @return {Layout} A new `Layout` containing current attributes and loaded resources contained in `this`.
     */
    clone: function () {
      this.storeConfiguration();

      var result = new Layout({
        configuration: this.get("configuration"),
        name: this.get("name") + " (Copy)",
      });

      result.serviceLayers = this.serviceLayers.slice();

      return result;
    },
  },
  {
    fields: {
      name: FieldUtils.fieldPrototype("name", "Name", "text"),
      description: FieldUtils.fieldPrototype(
        "description",
        "Description",
        "textarea"
      ),
      createdAt: FieldUtils.fieldPrototype("createdAt", "Created At", "date", {
        viewOptions: {
          type: "fromnow",
        },
        sortable: true,
      }),
      updatedAt: FieldUtils.fieldPrototype("updatedAt", "Updated At", "date", {
        viewOptions: {
          type: "fromnow",
        },
        sortable: true,
      }),
    },
    /**
     * Creates a configuration which simply loads the given services.
     * @param {Array|MapServiceBase} services The services to load. This should either be a single MapService or an array of MapService models.
     * @return {Layout} A `Layout` which will load the given services when loadConfiguration is invoked.
     * @static
     */
    CreateDefault: function (services) {
      if (!_.isArray(services)) {
        services = [services];
      }

      return new Layout({
        name: Layout.DefaultName(session),
        configuration: _.map(services, function (service) {
          return {
            id: service.id,
            layers: [],
          };
        }),
      });
    },
    /**
     * Gets the default name for a layer configuration.
     * @static
     * @param session A reference to the current session.
     * @return {String} The default name.
     */
    DefaultName: function (session) {
      return session.user.get("PrimaryName") + "'s Layers";
    },
    /**
     * Refreshes the MapService data source shared by layouts.
     * @return {Promise} A promise which resolves with the MapServiceCollection or fails with an error.
     */
    LoadServices: loadServices,
  }
);

export default Layout;



//////////////////
// WEBPACK FOOTER
// ./public/js/app/models/Layout.js
// module id = 134
// module chunks = 1